import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Row, Col, Form, Alert, Button, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { Bars } from 'react-loader-spinner';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formFields } from './EditProfileConfig.js';
import service from '../../services/service.js';
import HeaderTwo from '../HeaderTwo.js';
import { BreadcrumbBox } from '../common/Breadcrumb.js';
import './EditProfile.css';

const EditProfile = (props) => {
  let id = props.match.params.id;

  const { t } = useTranslation();
  const history = useHistory();

  const [allCountries, setAllCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allQualifications, setAllQualifications] = useState([]);
  const [allDesignations, setAllDesignations] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [allDistrict, setAllDistrict] = useState([]);

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [fieldStates, setFieldStates] = useState({
    otherCountryVisible: false,
  });

  // --- OTP Verification States ---
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState({ message: "" });
  const [otpData, setOtpData] = useState({
    showModal: false,
    sendTo: "",
    otpCode: "",
    isOtpResend: false,
    isEmailVerify: false,
  });
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const inputs = useRef([]);

  // Check if current user is inactive
  const isInactiveUser = String(formData?.isactive).toLowerCase() === "false";
  const isUserActive = String(formData?.isactive).toLowerCase() === "true";

  useEffect(() => {
    fetchUserDetails();
    getAllCountry();
    getAllStates();
    getAllDistrictByStateId();
    getAllQualifications();
    getAllDesignations();
  }, [id]);

  const fetchUserDetails = () => {
    setPageLoading(true);
    service.getUserProfileById(id)
      .then((response) => {
        const data = response.data;

        // 1. Clean Gender
        const validGenders = ['Male', 'Female', 'Other'];
        if (data.gender && !validGenders.includes(data.gender)) {
          data.gender = "";
        }

        // 2. Clean Marital Status
        const validMarital = ['Single', 'Married'];
        if (data.maritalStatus && !validMarital.includes(data.maritalStatus)) {
          data.maritalStatus = "";
        }

        // 3. Clean numeric dropdown values
        if (data.districtId === 0) data.districtId = "";
        if (data.stateId === 0) data.stateId = "";
        if (data.countryId === 0) data.countryId = "";
        if (data.desgId === 0) data.desgId = "";
        if (data.qualId === 0) data.qualId = "";

        setFormData(data);

        // If user is ALREADY active ("true"), email is treated as verified
        const inactive = String(data.isactive).toLowerCase() === "false";
        setOtpData(prev => ({
          ...prev,
          isEmailVerify: !inactive
        }));

        setPageLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching profile data:', err);
        setPageLoading(false);
      });
  };

  // --- OTP Handlers ---
  const onClickSendOtpHandler = (e, emailToSend) => {
    e.preventDefault();
    if (!emailToSend || otpLoading) return;

    // Validate email format first
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToSend)) {
      Swal.fire({
        icon: "error",
        title: t("warning") || "Invalid Email",
        text: "Please enter a valid email address before requesting OTP."
      });
      return;
    }

    setOtpLoading(true);
    const payload = { sendTo: emailToSend };

    service.sendOTP(payload)
      .then((resp) => {
        if (resp.status === 200) {
          if (resp.data?.verified === true) {
            Swal.fire({
              icon: 'info',
              title: "OTP Status",
              text: "This Email Id is already verified."
            });
            setOtpData(prev => ({
              ...prev,
              showModal: false,
              isEmailVerify: true,
              sendTo: ""
            }));
          } else {
            setOtpData(prev => ({
              ...prev,
              sendTo: emailToSend,
              showModal: true,
              otpCode: ""
            }));
            setOtp(["", "", "", "", "", ""]);
            setOtpError({ message: "" });
          }
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "OTP Status",
          text: error.response?.data?.message || "Failed to send OTP. Please try again."
        });
      })
      .finally(() => {
        setOtpLoading(false);
      });
  };

  const handleOtpBoxChange = (element, index) => {
    const value = element.value.replace(/\D/, "");
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5 && inputs.current[index + 1]) {
      inputs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputs.current[index - 1]) {
      inputs.current[index - 1].focus();
    }
  };

  const onClickVerify = () => {
    if (verifyLoading) return;

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setOtpError({ message: "Please enter a valid 6-digit OTP" });
      return;
    }

    setVerifyLoading(true);
    const payload = {
      sendTo: otpData.sendTo,
      otpCode: otpCode
    };

    service.validateOTP(payload)
      .then((resp) => {
        if (resp.status === 200 && resp.data?.verified) {
          setOtpData(prev => ({
            ...prev,
            isEmailVerify: true,
            showModal: false,
            sendTo: "",
            otpCode: ""
          }));
          setOtp(["", "", "", "", "", ""]);
          setOtpError({ message: "" });

          Swal.fire({
            icon: "success",
            title: "Verified!",
            text: "Email ID verified successfully. You can now update your profile."
          });
        } else {
          setOtpError({ message: resp.data?.message || "Invalid OTP entered." });
        }
      })
      .catch((error) => {
        setOtpError({ message: error.response?.data?.message || "OTP Verification failed." });
      })
      .finally(() => {
        setVerifyLoading(false);
      });
  };

  const onClickResendButton = () => {
    if (resendLoading) return;
    setOtp(["", "", "", "", "", ""]);
    setResendLoading(true);

    service.sendOTP({ sendTo: otpData.sendTo })
      .then((resp) => {
        if (resp.status === 200) {
          setOtpData(prev => ({
            ...prev,
            isOtpResend: true
          }));
          setOtpError({ message: resp.data?.message || "OTP resent successfully." });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "OTP Status",
          text: error.response?.data?.message || "Failed to resend OTP."
        });
      })
      .finally(() => {
        setResendLoading(false);
      });
  };

  // --- Form Input Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If user changes their email after verifying, reset verification
    if (name === 'email' && isInactiveUser) {
      setOtpData(prev => ({ ...prev, isEmailVerify: false }));
    }

    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));

    // Dynamic field state dependencies
    formFields.forEach((field) => {
      if (field.enabledDependencies && field.enabledDependencies.field === name) {
        const shouldEnable = field.enabledDependencies.condition(value);
        setFieldStates(prev => ({
          ...prev,
          [`${field.name}Disabled`]: !shouldEnable,
        }));
      }

      if (field.visibilityDependencies && field.visibilityDependencies.field === name) {
        const shouldShow = field.visibilityDependencies.condition(value);
        setFieldStates(prev => ({
          ...prev,
          [`${field.name}Visible`]: shouldShow,
        }));
      }

      if (field.requireDependencies && field.requireDependencies.field === name) {
        const shouldRequire = field.requireDependencies.condition(value);
        setFieldStates(prev => ({
          ...prev,
          [`${field.name}Required`]: shouldRequire,
        }));

        if (shouldRequire) {
          const validationError = validateField(field.name, formData[field.name] || '');
          setErrors(prev => ({ ...prev, [field.name]: validationError }));
        } else {
          setErrors(prev => ({ ...prev, [field.name]: null }));
        }
      }
    });
  };

  const getAllCountry = () => {
    service.getCountry().then(res => setAllCountries(res?.data)).catch(() => { });
  };

  const getAllStates = () => {
    service.getState().then(res => setAllStates(res?.data)).catch(() => { });
  };

  const getAllQualifications = () => {
    service.RegisterQualification().then(res => setAllQualifications(res?.data)).catch(() => { });
  };

  const getAllDesignations = () => {
    service.RegisterDesignation().then(res => setAllDesignations(res?.data)).catch(() => { });
  };

  const getAllDistrictByStateId = () => {
    service.getDistrict(33).then(res => setAllDistrict(res?.data)).catch(() => { });
  };

  const validateField = (name, value) => {
    const field = formFields.find((f) => f.name === name);
    if (!field) return null;

    const isRequired = fieldStates[`${name}Required`] || field.required;

    if (isRequired) {
      if (field.type === 'select') {
        if (value === undefined || value === null || value === '' || value === 0 || value === '0') {
          return `${field.label} is required`;
        }
      }
      if (field.type !== 'select' && field.type !== 'file') {
        if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
          return `${field.label} is required`;
        }
      }
      if (field.type === 'file' && (!value || value.length === 0)) {
        return `${field.label} is required`;
      }
    }

    if (value && field.validation?.regex && !field.validation.regex.test(value)) {
      return field.validation.errorMessage;
    }

    if (value && field.validation?.custom && !field.validation.custom(value)) {
      return field.validation.errorMessage;
    }

    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Guard check: Inactive users MUST verify email with OTP before update
    if (isInactiveUser && !otpData.isEmailVerify) {
      Swal.fire({
        icon: 'warning',
        title: t('warning') || 'Verification Required',
        text: 'Please verify your Email ID with OTP before updating your profile.',
      });
      return;
    }

    const validationErrors = {};
    formFields.forEach((field) => {
      const isVisible = fieldStates[`${field.name}Visible`] !== false;
      if (isVisible) {
        const error = validateField(field.name, formData[field.name]);
        if (error) {
          validationErrors[field.name] = error;
        }
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill in all required fields properly."
      });
      return;
    }

    setLoading(true);
    setPageLoading(true);

    const jsonPayload = { ...formData };

    // Set isactive to "true" upon successful verification update
    if (isInactiveUser && otpData.isEmailVerify) {
      jsonPayload.isactive = "true";
    }

    service.updateUser(jsonPayload)
      .then(async (response) => {
        if (response.status === 200) {
          setLoading(false);
          setPageLoading(false);
          Swal.fire({
            title: t("success_swal") || "Success",
            text: t("profile_update_success_desc") || "Profile updated successfully!",
            icon: "success",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            history.push("/");
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setPageLoading(false);

        if (err.response && err.response.data) {
          const { status, data } = err.response;
          if (status >= 400 && status < 500) {
            Swal.fire(t("warning"), data.message || data, "warning");
          } else {
            Swal.fire(t("error"), t("unexpected_error_message"), "error");
          }
        } else {
          Swal.fire(t("error"), t("network_error"), "error");
        }
      });
  };

  const groupedFields = formFields.reduce((groups, field) => {
    const { row = 1 } = field;
    if (!groups[row]) groups[row] = [];
    groups[row].push(field);
    return groups;
  }, {});

  return (
    <div className="edit-profile-page">
      <HeaderTwo />
      <BreadcrumbBox title={t('edit_profile')} />

      {pageLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <Bars height={40} width={40} color="#0C122Fff" ariaLabel="bars-loading" />
        </div>
      )}

      <Container fluid className="edit-profile-area">
        <Card className="edit-profile-card">
          <Card.Header className="text-center border-0 bg-transparent pt-4">
            <div className="edit-profile-title">
              <h2>{t('edit_profile')}</h2>
              {isInactiveUser && (
                <Alert variant="warning" className="mt-3 text-start">
                  <strong>Notice:</strong> Your account is currently inactive. Please enter and verify your <strong>Email ID</strong> via OTP to enable profile updates.
                </Alert>
              )}
            </div>
          </Card.Header>
          <Card.Body className="px-4 py-4">
            <Form onSubmit={handleSubmit}>
              {Object.entries(groupedFields).map(([row, fields]) => (
                <Row key={row} className="mb-3">
                  {fields.map((field) => {
                    const isVisible = fieldStates[`${field.name}Visible`] !== false;
                    const isDisabled = fieldStates[`${field.name}Disabled`] === true;

                    if (!isVisible) return null;

                    // Check if this is the email field and user is inactive
                    const isEmailField = field.name === 'email';

                    return (
                      <Col key={field.name} md={field.col || 6} sm={12}>
                        <Form.Group className="form-group">
                          <div className="d-flex align-items-center">
                            <Form.Label className="form-label mb-0">
                              {t(field.label)}
                              {field.required && <span className="text-danger">*</span>}
                            </Form.Label>
                          </div>

                          {/* 1. Email Field Handling */}
                          {isEmailField ? (
                            <div className="d-flex mt-1">
                              <Form.Control
                                className={`form-input ${(field.isDisabled || isUserActive || (isInactiveUser && otpData.isEmailVerify)) ? 'disabled' : ''}`}
                                type="email"
                                name="email"
                                placeholder={field.placeholder || "Enter your email"}
                                value={formData.email || ''}
                                onChange={handleInputChange}
                                isInvalid={!!errors.email}
                                // DISABLED if active OR if already verified via OTP
                                disabled={field.isDisabled || isUserActive || (isInactiveUser && otpData.isEmailVerify)}
                              />

                              {/* If user is ALREADY active, show verified badge */}
                              {isUserActive && (
                                <div className="d-flex align-items-center ms-2 text-success" title="Verified Email">
                                  <i className="fa fa-check-circle fs-3 me-1"></i>
                                  <span className="fw-bold small">{t('verified') || 'Verified'}</span>
                                </div>
                              )}

                              {/* If user is INACTIVE, show Send OTP button until verified */}
                              {isInactiveUser && (
                                otpData.isEmailVerify ? (
                                  <div className="d-flex align-items-center ms-2 text-success">
                                    <i className="fa fa-check-circle fs-3 me-1"></i>
                                    <span className="fw-bold small">{t('verified') || 'Verified'}</span>
                                  </div>
                                ) : (
                                  <Button
                                    variant="success"
                                    className="ms-2 text-nowrap"
                                    onClick={(e) => onClickSendOtpHandler(e, formData.email)}
                                    disabled={otpLoading || !formData.email || !!errors.email}
                                  >
                                    {otpLoading ? "Sending..." : "Send OTP"}
                                  </Button>
                                )
                              )}
                            </div>
                          ) : field.type === 'select' ? (
                            <Form.Select
                              className={`form-select ${field.isDisabled ? 'disabled' : ''}`}
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={handleInputChange}
                              isInvalid={!!errors[field.name]}
                              disabled={field.isDisabled}
                            >
                              <option value="">{`Select ${field.label}`}</option>
                              {(() => {
                                const options =
                                  field.name === 'qualId' ? allQualifications :
                                    field.name === 'desgId' ? allDesignations :
                                      field.name === 'countryId' ? allCountries :
                                        field.name === 'stateId' ? allStates :
                                          field.name === 'orgId' ? allOrganizations :
                                            field.name === 'districtId' ? allDistrict :
                                              field.options || [];

                                return options.map((option, index) => (
                                  <option
                                    key={index}
                                    value={
                                      option.qualId ||
                                      option.desgId ||
                                      option.countryId ||
                                      option.stateId ||
                                      option.orgId ||
                                      option.districtId ||
                                      option.value ||
                                      option
                                    }
                                  >
                                    {option.qualification ||
                                      option.designation ||
                                      option.countryName ||
                                      option.stateName ||
                                      option.orgName ||
                                      option.districtName ||
                                      option.label ||
                                      option}
                                  </option>
                                ));
                              })()}
                            </Form.Select>
                          ) : field.type === 'date' ? (
                            <Form.Control
                              className={`form-input ${field.isDisabled ? 'disabled' : ''}`}
                              type="date"
                              name={field.name}
                              value={
                                formData[field.name]
                                  ? /^\d{4}-\d{2}-\d{2}$/.test(formData[field.name])
                                    ? formData[field.name]
                                    : new Date(formData[field.name].split('-').reverse().join('-'))
                                      .toISOString()
                                      .split('T')[0]
                                  : ''
                              }
                              onChange={handleInputChange}
                              isInvalid={!!errors[field.name]}
                              disabled={field.isDisabled}
                            />
                          ) : (
                            <Form.Control
                              className={`form-input ${field.isDisabled ? 'disabled' : ''}`}
                              type={field.type}
                              name={field.name}
                              placeholder={field.placeholder}
                              value={formData[field.name] || ''}
                              onChange={handleInputChange}
                              isInvalid={!!errors[field.name]}
                              disabled={field.isDisabled}
                              maxLength={field.maxLength}
                            />
                          )}

                          <Form.Control.Feedback type="invalid">
                            {errors[field.name]}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    );
                  })}
                </Row>
              ))}

              <div className="form-footer mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || (isInactiveUser && !otpData.isEmailVerify)}
                  title={isInactiveUser && !otpData.isEmailVerify ? "Please verify your email before updating" : ""}
                >
                  {loading ? t('loading') : t('update_profile')}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      {/* --- OTP Verification Modal --- */}
      <Modal
        centered
        show={otpData.showModal}
        onHide={() => {
          setOtpData(prev => ({
            ...prev,
            showModal: false,
            sendTo: "",
            otpCode: "",
            isOtpResend: false
          }));
          setOtpError({ message: "" });
          setOtp(["", "", "", "", "", ""]);
        }}
        backdrop="static"
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ textAlign: "center", width: "100%" }}>
            Enter OTP
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="text-center">
            <p className="text-muted mb-4">
              Please enter the 6-digit code sent to <strong>{otpData.sendTo}</strong>.
            </p>

            {otpError.message && (
              <p className={otpData.isOtpResend ? "text-success mb-4" : "text-danger mb-4"}>
                {otpError.message}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px", marginBottom: "20px" }}>
              {otp.map((digit, index) => (
                <Form.Control
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  ref={(el) => (inputs.current[index] = el)}
                  onChange={(e) => handleOtpBoxChange(e.target, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  className="text-center fs-4 fw-semibold border rounded shadow-sm otp-input"
                  style={{
                    width: "45px",
                    height: "55px",
                    textAlign: "center",
                    fontSize: "20px",
                    borderRadius: "8px",
                    border: "1px solid #ced4da",
                    outline: "none"
                  }}
                />
              ))}
            </div>

            <Button
              onClick={onClickVerify}
              variant="success"
              size="lg"
              className="px-5 rounded fw-bold"
              disabled={verifyLoading}
            >
              {verifyLoading ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </Modal.Body>

        <Modal.Footer style={{ justifyContent: "center" }}>
          <Button
            variant="link"
            className="text-decoration-none fw-semibold"
            onClick={onClickResendButton}
            disabled={resendLoading}
          >
            {resendLoading ? "Sending..." : "Resend OTP"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditProfile;